import { AxiosResponse } from "axios";
import { axiosInstance } from "./axiosInstance";
import { ScheduleDTO } from "../../models/ScheduleDTO";
import { ScheduleHistoryDTO, ScheduleNoteDTO } from "../../models/ScheduleHistoryDTO";
import { PaginatedResponse } from "../../models/common/PaginatedResponseDTO";
import { SessionDTO } from "../../models/SessionDTO";
import { AvailableScheduleDTO } from "../../models/EnrollmentHistoryDTO";

const RESOURCE_URL_V2 = "/admin/campus/";

export class ScheduleService {
	/*
		Historial del horario: bitacora de cambios, rastro de aulas y de maestros.
		(Esteban, 2026-09-21)
	*/
	getScheduleHistory = async (campusId: number, scheduleId: number): Promise<AxiosResponse<ScheduleHistoryDTO>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/schedules/${scheduleId}/_auditLog`;
		return await axiosInstance.get<ScheduleHistoryDTO>(uri);
	}

	getScheduleNotes = async (campusId: number, scheduleId: number): Promise<AxiosResponse<ScheduleNoteDTO[]>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/schedules/${scheduleId}/_notes`;
		return await axiosInstance.get<ScheduleNoteDTO[]>(uri);
	}

	addScheduleNote = async (campusId: number, scheduleId: number, note: string): Promise<AxiosResponse<ScheduleNoteDTO[]>> => {
		const uri = `${RESOURCE_URL_V2}${campusId}/schedules/${scheduleId}/_notes`;
		return await axiosInstance.post<ScheduleNoteDTO[]>(uri, {note});
	}


	/** Schedules where a (new or existing) student can be enrolled right now, per the sign-up rules. */
	getAvailableSchedules = async (campusId: number, studentId?: number | null): Promise<AxiosResponse<AvailableScheduleDTO[]>> => {
		const query = studentId ? `?studentId=${studentId}` : "";
		return axiosInstance.get<AvailableScheduleDTO[]>(`${RESOURCE_URL_V2}${campusId}/schedules/_available${query}`);
	}

	getSchedulesByCampusId = async (campusId: number, perPage: number, criteria?: any): Promise<AxiosResponse<PaginatedResponse<ScheduleDTO>>> => {
		let uri = `${RESOURCE_URL_V2}${campusId}/_schedules?perPage=${perPage}`;
		if (criteria && typeof criteria === 'object' && Object.keys(criteria).length > 0) {
			try {
				const criteriaJson = JSON.stringify(criteria);
				uri = `${uri}&criteria=${encodeURIComponent(criteriaJson)}`;
			} catch (error) {
				console.error("Error serializando criteria as JSON:", error);
				throw new Error("Invalid criteria format");
			}
		}
		const response = await axiosInstance.get<PaginatedResponse<ScheduleDTO>>(uri);
		return response;
	}
	
	getScheduleSessions = async (campusId: number, scheduleId: number): Promise<AxiosResponse<SessionDTO[]>> => {
		let uri = `${RESOURCE_URL_V2}${campusId}/schedules/${scheduleId}/_sessions`;
		const response = await axiosInstance.get<SessionDTO[]>(uri);
		return response;
	}
	
	/*
		Fin previsto de un horario (Académico R2): inicio + días de clase ("0,2,4", 0 = lunes) + módulo → {endDate, sessions,
		perWeek, source}. billableMonths solo cuenta en talleres/eventos (sin plan del curso).
	*/
	getScheduleEndDate = async (campusId: number, startDate: string, weekDays: string, levelId?: number | null, scheduleId?: number | null, billableMonths: number = 0): Promise<AxiosResponse> => {
		let uri = `${RESOURCE_URL_V2}${campusId}/schedules/_endDate`;
		const params = new URLSearchParams();
		params.append('startDate', startDate);
		params.append('weekDays', weekDays);
		if (levelId) { params.append('levelId', String(levelId)); }
		if (scheduleId) { params.append('scheduleId', String(scheduleId)); }
		params.append('billableMonths', String(billableMonths || 0));
		const response = await axiosInstance.get(uri, { params });
		return response;
	}

	getPaginatedSchedules = async (uri: string,  criteria?: any): Promise<AxiosResponse<PaginatedResponse<ScheduleDTO>>> => {
		if (criteria && typeof criteria === 'object' && Object.keys(criteria).length > 0) {
			try {
				const criteriaJson = JSON.stringify(criteria);
				uri = `${uri}&criteria=${encodeURIComponent(criteriaJson)}`;
			} catch (error) {
				console.error("Error serializando criteria as JSON:", error);
				throw new Error("Invalid criteria format");
			}
		}
        let response = await axiosInstance.get<PaginatedResponse<ScheduleDTO>>(uri);
        return response;
    }
}